import React from 'react'
import { CSSTransition } from 'react-transition-group'
import cx from 'classnames'
import { pure } from 'recompose'
import { connect } from 'react-redux'
import { useValueState } from 'webmango-core/state'

import { stopPropagation } from '../utils/events'
import './Modal.css'

const Modals = useValueState('modals')

const MODAL_APPEAR_TIMEOUT = 300

export const Modal = pure(function Modal ({
  shouldAutoClose,
  className,
  closeModal,
  isOpen,
  isBlocking,
  children,
  ...props
}) {
  const useOverlay = isOpen && closeModal
  return (
    <div className={cx('Modal', className, { isOpen })}>
      {useOverlay && (
        <React.Fragment>
          <Overlay
            onTrigger={!isBlocking ? closeModal : Function.prototype}
          />
          <AutoCloser
            isOpen={isOpen}
            shouldAutoClose={shouldAutoClose}
            onTrigger={closeModal}
          />
        </React.Fragment>
      )}
      <CSSTransition
        classNames='modal'
        className='modalInner'
        in={!!isOpen}
        timeout={MODAL_APPEAR_TIMEOUT}
        appear
        mountOnEnter
        unmountOnExit
        {...props}
      >
        <main>{children}</main>
      </CSSTransition>
    </div>
  )
})

export default Modal

/**
 * Autoclose behaviour
 */

export function makeAutoCloseOnChange (key) {
  return (prev, next) => prev.location[key] !== next.location[key]
}

export const autoCloseOnChangeKey = makeAutoCloseOnChange('key')
export const autoCloseOnChangePath = makeAutoCloseOnChange('pathname')

const AutoCloser = connect(({ router }) => {
  return {
    // read location via connect instead of withRouter
    // withRouter won't rerender on location change
    location: router.location
  }
})(class CloseOnNavigate extends React.Component {
  static defaultProps = {
    shouldAutoClose: autoCloseOnChangeKey
  }

  componentDidUpdate (prevProps) {
    if (this.props.isOpen && this.props.shouldAutoClose(prevProps, this.props)) {
      this.props.onTrigger()
    }
  }

  render () {
    return null
  }
})

/**
 * Overlay
 */

export class Overlay extends React.PureComponent {
  static layers = new Set()
  static index = 0
  onKeyDown = (event) => {
    const topLayer = [...Overlay.layers].pop()
    if (this.state.layer !== topLayer) return
    if (event.code === 'Escape') {
      this.props.onTrigger(event)
    }
  }

  componentDidMount () {
    Overlay.index++
    const layer = Overlay.index %= 16
    if (Overlay.layers.has(layer)) console.warn(`Overlay.layers: layer exists? ${layer}`)
    Overlay.layers.add(layer)
    this.setState({ layer })
    window.addEventListener('keydown', this.onKeyDown)
  }

  componentWillUnmount () {
    Overlay.layers.delete(this.state.layer)
    window.removeEventListener('keydown', this.onKeyDown)
  }

  onMouseDown = (event) => {
    if (event.button !== 0) return
    this.props.onTrigger(event)
  }

  render () {
    const { className, onTrigger, ...props } = this.props
    return (
      <div
        className={cx('Overlay', className)}
        {...props}
        onMouseDown={this.onMouseDown}
        onWheel={this.props.onTrigger}
      />
    )
  }
}

/**
 * ModalAPI
 */

export function makeModalAPI (key) {
  if (!key) throw new Error('key required')
  return connect((state, { name = 'default' }) => {
    const s = select(state, { name, key })
    return {
      [`${key}IsOpen`]: s.isOpen,
      [`${key}Value`]: s.value
    }
  }, (dispatch, { name = 'default' }) => {
    const s = API({ name, key })
    return {
      [`${key}Close`]: s.close,
      [`${key}Open`]: s.open
    }
  })
}

export const select = ({ modals }, { key, name = 'default' }) => {
  const modal = modals[key] || {}
  const isOpen = name && modal.name === name
  const value = isOpen && modal.value
  return {
    isOpen,
    value
  }
}

export const API = ({ key, name }) => {
  return {
    close (value) {
      if (value && value.eventPhase) {
        stopPropagation(value)
      }
      Modals.delete(key)
    },
    open (value, options = {}) {
      if (options.closeOthers) {
        Modals.clear()
      }
      if (value && value.eventPhase) {
        stopPropagation(value)
        value = undefined // ignore events
      }
      Modals.set({
        key,
        value: {
          name,
          value
        }
      })
    }
  }
}

export function cleanProps (key, props) {
  const newProps = {
    ...props
  }
  delete newProps[`${key}IsOpen`]
  delete newProps[`${key}Value`]
  delete newProps[`${key}Close`]
  delete newProps[`${key}Open`]
  return newProps
}

export {
  makeModalAPI as makeAPI
}



// WEBPACK FOOTER //
// ./src/components/Modal.js